import * as React from "react"
import { Link } from "react-router-dom"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import { useIsSectionCollapsed } from "src/frontend/components/OS/Applications/Reports/Report/Options/CollapsingSections"
import {
  AGING_COLUMN_OFFSET,
  AMOUNT,
  columnsByComponentSize,
} from "src/frontend/components/Shared/Layout/Components/Statements/AgingReport/AgingColumns"
import { CollapsibleRow } from "src/frontend/components/Shared/Layout/Components/Statements/CollapsibleRow"
import {
  RowStyled,
  Title,
} from "src/frontend/components/Shared/Layout/Components/Statements/shared"
import { useStatementComponentSize } from "src/frontend/components/Shared/Layout/Components/Statements/StatementContext"
import { HeaderStyled } from "src/frontend/components/Shared/Layout/Components/Statements/StatementHeader"
import {
  ParentCategorySpacing,
  Row,
  type TitleProps,
} from "src/frontend/components/Shared/Layout/Components/Statements/StatementRowShared"
import { useRowDetailsLinks } from "src/frontend/components/Shared/Layout/Components/Statements/useRowDetailsLinks"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"

const LeftAlignedHeader = styled(HeaderStyled)`
  white-space: nowrap;
  text-align: left;
`

const HeaderRow = styled(Row)`
  padding-right: 0;
`

const HeaderContainer = styled.div<{ $paddingLeft: number; $columnSpan: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columnSpan}, minmax(0, 1fr));
  grid-column: span ${(props) => props.$columnSpan};
  padding-left: ${(props) => props.$paddingLeft}px;
`

const StyledCollapsibleRow = styled(CollapsibleRow)`
  font-weight: ${fonts.weight.heavy};

  &:hover {
    background: ${colors.secondary10};
  }
`

const StyledHeader = styled(HeaderStyled)<{ $textAlign: string }>`
  white-space: nowrap;
  text-align: ${({ $textAlign }) => $textAlign};
`

export const ParentAgingTitle: React.FC<TitleProps> = ({
  id,
  depth,
  title: { title },
  details,
}) => {
  const isSectionCollapsed = useIsSectionCollapsed(id)
  const { disabled } = useRowDetailsLinks(details)
  const componentSize = useStatementComponentSize()
  const columns = columnsByComponentSize(componentSize)
  const pathGenerator = useFrontendPathGenerator()
  const vendorUrl = pathGenerator(routes.partyDetails, {
    partyId: id.replace("-title", ""),
    partyRole: "vendor",
  })

  let content = (
    <Title depth={depth} disabled={disabled} data-context-id={id}>
      {title}
    </Title>
  )

  if (!isSectionCollapsed) {
    content = <ParentCategorySpacing>{content}</ParentCategorySpacing>
  }

  if (componentSize.isExtraLarge || componentSize.isLarge) {
    return (
      <>
        <StyledCollapsibleRow rowId={id} depth={depth}>
          <Link to={vendorUrl}>
            <RowStyled id={id} depth={depth}>
              {content}
            </RowStyled>
          </Link>
        </StyledCollapsibleRow>
        <HeaderRow rowId={id} depth={depth}>
          <HeaderContainer $columnSpan={AGING_COLUMN_OFFSET} $paddingLeft={32}>
            {columns.slice(0, AGING_COLUMN_OFFSET).map((column) => (
              <LeftAlignedHeader key={column}>{column}</LeftAlignedHeader>
            ))}
          </HeaderContainer>
          {columns.slice(AGING_COLUMN_OFFSET).map((column) => (
            <HeaderStyled key={column}>{column}</HeaderStyled>
          ))}
        </HeaderRow>
      </>
    )
  }
  if (componentSize.isMedium) {
    return (
      <>
        <StyledCollapsibleRow rowId={id} depth={depth}>
          <Link to={vendorUrl}>
            <RowStyled id={id} depth={depth}>
              {content}
            </RowStyled>
          </Link>
        </StyledCollapsibleRow>
        <Row
          rowId={id}
          depth={depth}
          css={`
            padding-right: 0;
            grid-template-columns: repeat(${columns.length}, minmax(0, 1fr));
          `}
        >
          <HeaderContainer $columnSpan={columns.length} $paddingLeft={28}>
            {columns.map((column) => (
              <StyledHeader key={column} $textAlign={column === AMOUNT ? "right" : "left"}>
                {column}
              </StyledHeader>
            ))}
          </HeaderContainer>
        </Row>
      </>
    )
  }
}
